import { useState, useEffect } from 'react'

import { ActionIcon, Box, Collapse, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'

import { utcMonthToLocalDate } from '../../utils'

import AddEmploymentDrawer from './components/AddEmploymentDrawer'
import AddUnemploymentDrawer from './components/EditUnemploymentModal'
import EmploymentTable from './components/EmploymentTable'

const EmploymentCard = ({
  employmentExperiences,
  handleUpdateApplication,
  applicationId,
  applicantId,
  verificationPeriodMonths,
}) => {
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)
  const [tempExperiences, setTempExperiences] = useState(
    employmentExperiences || []
  )
  const [currentIndex, setCurrentIndex] = useState<number>(
    tempExperiences.length > 0 ? 0 : null
  )
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [creationMode, setCreationMode] = useState<
    'employment' | 'unemployment'
  >('employment')
  const [gapDates, setGapDates] = useState<{
    startDate?: string
    endDate?: string
  } | null>(null)

  // Sync tempExperiences with the prop when it changes (e.g., after deletions from other components)
  // But only when the edit modal is not open to avoid losing temp changes
  useEffect(() => {
    if (!editModalOpened) {
      setTempExperiences(employmentExperiences || [])
    }
  }, [employmentExperiences, editModalOpened])

  const _handleCancel = () => {
    setTempExperiences([...employmentExperiences])
  }

  const handleSave = (experiences = null) => {
    let updates = {}
    const temp = experiences || tempExperiences
    updates = {
      employmentExperiences: temp.map((item) => {
        return {
          ...(item.employmentExperienceId && {
            employmentExperienceId: item.employmentExperienceId,
          }),
          companyName: item.companyName,
          startDate: item.startDate,
          address: {
            street: item.address?.street || '',
            city: item.address?.city || '',
            state: item.address?.state || '',
            zipCode: item.address?.zipCode || '',
            country: item.address?.country || '',
          },
          dotNumber: item.dotNumber,
          email: item.email,
          contactName: item.contactName,
          isUnemployment: item.isUnemployment,
          unemploymentReason: item.unemploymentReason,
          ...(item.endDate !== undefined &&
            item.endDate !== '' && { endDate: item.endDate }),
          ...(item.position !== undefined &&
            item.position !== '' && { position: item.position }),
          ...(item.reasonForLeaving !== undefined && {
            reasonForLeaving: item.reasonForLeaving,
          }),
          ...(item.isDriverTerminated !== undefined && {
            isDriverTerminated: item.isDriverTerminated,
          }),
          ...(item.isDriverSubjectToFMCSRs !== undefined && {
            isDriverSubjectToFMCSRs: item.isDriverSubjectToFMCSRs,
          }),
          ...(item.isJobDesignatedAsSafetySensitiveFunctionUnderDOT !==
            undefined && {
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              item.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
          }),
          ...(item.shouldContactEmployer !== undefined && {
            shouldContactEmployer: item.shouldContactEmployer,
          }),
          ...(item.phone !== undefined && { phone: item.phone }),
          ...(item.faxNumber !== undefined && { faxNumber: item.faxNumber }),
          ...(item.dotNumber !== undefined && { dotNumber: item.dotNumber }),
          ...(item.equipmentTypes !== undefined && {
            equipmentTypes: item.equipmentTypes,
          }),
        }
      }),
    }
    handleUpdateApplication(updates).then((res) => {
      if (!res.errors) {
        const experiences = res.data.updateApplication.employmentExperiences
        setTempExperiences([...experiences])
        setCurrentIndex(null)
        closeEditModal()
      }
    })
  }

  const handleDelete = (id) => {
    const temp = [...tempExperiences]
    const index = temp.findIndex((item) => item.employmentExperienceId === id)
    temp.splice(index, 1)
    setTempExperiences(temp)
    handleSave(temp)
  }

  const addEmployment = (startDate?: string, endDate?: string) => {
    setGapDates(startDate && endDate ? { startDate, endDate } : null)
    setIsCreatingNew(true)
    setCreationMode('employment')
    openEditModal()
  }

  const addUnemployment = (startDate?: string, endDate?: string) => {
    setGapDates(startDate && endDate ? { startDate, endDate } : null)
    setIsCreatingNew(true)
    setCreationMode('unemployment')
    openEditModal()
  }

  const handleCloseModal = () => {
    setIsCreatingNew(false)
    setGapDates(null)
    closeEditModal()
  }

  const _isCurrentItemUnemployment =
    tempExperiences[currentIndex]?.isUnemployment

  const [sectionOpened, setSectionOpened] = useState(true)

  return (
    <div
      className={`flex w-full flex-col gap-4 rounded-lg border border-doubleNickel-gray-200 p-4`}
    >
      <div className="text-md flex flex-row items-center justify-between font-semibold text-doubleNickel-black">
        <div
          className="flex cursor-pointer flex-row items-center gap-4"
          onClick={() => setSectionOpened((prev) => !prev)}
        >
          <ActionIcon variant="subtle">
            <IconChevronDown
              className={`h-4 w-4 ${
                !sectionOpened && '-rotate-90'
              } fill-none stroke-doubleNickel-gray-500 transition-all`}
            />
          </ActionIcon>
          Employment History
        </div>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <Box className="font-lg flex flex-row items-center justify-between font-bold text-doubleNickel-gray-700">
              <ActionIcon className="h-8 w-8 border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm hover:bg-doubleNickel-gray-200">
                <IconAdd className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </Box>
          </Menu.Target>
          <Menu.Dropdown className="w-40">
            <Menu.Item
              className={` text-doubleNickel-gray-700`}
              onClick={() => addEmployment()}
            >
              Add previous employment
            </Menu.Item>
            <Menu.Item
              className="text-doubleNickel-gray-700"
              onClick={() => addUnemployment()}
            >
              Add unemployment period
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
      <Collapse in={sectionOpened} transitionDuration={400}>
        <EmploymentTable
          experiences={employmentExperiences || []}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          handleDelete={handleDelete}
          applicationId={applicationId}
          applicantId={applicantId}
          verificationPeriodMonths={verificationPeriodMonths}
          onAddEmployment={addEmployment}
          onAddUnemployment={addUnemployment}
        />
        {editModalOpened && isCreatingNew && creationMode === 'employment' && (
          <AddEmploymentDrawer
            opened={editModalOpened}
            close={handleCloseModal}
            applicationId={applicationId}
            applicantId={applicantId}
            initialStartDate={utcMonthToLocalDate(gapDates?.startDate)}
            initialEndDate={utcMonthToLocalDate(gapDates?.endDate)}
          />
        )}
        {editModalOpened &&
          isCreatingNew &&
          creationMode === 'unemployment' && (
            <AddUnemploymentDrawer
              opened={editModalOpened}
              close={handleCloseModal}
              applicationId={applicationId}
              applicantId={applicantId}
              initialStartDate={utcMonthToLocalDate(gapDates?.startDate)}
              initialEndDate={utcMonthToLocalDate(gapDates?.endDate)}
            />
          )}
        {/* TODO: Old editing logic - will be removed once fully migrated to individual editing */}
      </Collapse>
    </div>
  )
}

export default EmploymentCard
